import { router } from '@/main';
import { API_URL } from './consts';
import { defaultHeaders } from './headers';
import { FetchGetOptions, FetchOptions, FetchResponse } from './types';
import { getURLSearchParams } from './utils';
import { setStoredToken, setStoredUser } from '@/auth';
import { ZodApiSchema } from 'utils';

function logoutOnExpiredSession() {
  router.navigate({ to: '/login' });
  router.options.context.toast({
    title: 'Unauthorized',
    description: 'Your session has expired. Please log in again.',
    variant: 'destructive',
  });
  setStoredToken(null);
  setStoredUser(null);

  throw new Error('Unauthorized');
}

export const get = async <S extends ZodApiSchema>(
  url: string,
  schema: S,
  ...opts: FetchGetOptions<S>
): Promise<FetchResponse<S['response']>> => {
  const reqParams = opts[0] || ({} satisfies RequestInit);
  const urlParams = reqParams?.queryParams ? '?' + getURLSearchParams(reqParams.queryParams) : '';

  const res = await fetch(`${API_URL}/${url}${urlParams}`, {
    ...reqParams,
    headers: defaultHeaders(reqParams?.headers),
  });

  if (res.status === 401) {
    logoutOnExpiredSession();
  }

  return {
    ...res,
    res: {
      data: schema.response[res.status as keyof typeof schema.response]?.parse(await res.json()),
      status: res.status,
    },
  } as FetchResponse<S['response']>;
};

export const post = async <S extends ZodApiSchema>(
  url: string,
  _s: S,
  ...opts: FetchOptions<S>
): Promise<FetchResponse<S['response']>> => {
  const reqParams: RequestInit = (opts[0] || {}) as RequestInit;

  let newOpts: RequestInit = {
    method: 'POST',
    headers: {
      ...defaultHeaders(reqParams?.headers),
      'Content-Type': 'application/json',
    },
  };

  if (reqParams?.body) {
    newOpts = { ...reqParams, body: JSON.stringify(reqParams.body), ...newOpts };
  }

  const res = await fetch(`${API_URL}/${url}`, newOpts);

  if (res.status === 401) {
    logoutOnExpiredSession();
  }

  return {
    ...res,
    res: {
      data: await res.json(),
      status: res.status,
    },
  } as FetchResponse<S['response']>;
};

export const del = async <S extends ZodApiSchema>(
  url: string,
  schema: S,
  ...opts: FetchOptions<S>
): Promise<FetchResponse<S['response']>> => {
  const reqParams: RequestInit = (opts[0] || {}) as RequestInit;

  const res = await fetch(`${API_URL}/${url}`, {
    method: 'DELETE',
    headers: defaultHeaders(reqParams?.headers),
    ...reqParams,
  });

  if (res.status === 401) {
    logoutOnExpiredSession();
  }

  return {
    ...res,
    res: {
      data:
        res.status === 204
          ? undefined
          : schema.response[res.status as keyof typeof schema.response]?.parse(await res.json()),
      status: res.status,
    },
  } as FetchResponse<S['response']>;
};
